import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import Papa from 'papaparse';

const ImportCardsDialog = ({ open, onClose, onImport, boardId }) => {
  const [csvData, setCsvData] = useState([]);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when dialog is closed
  useEffect(() => {
    if (!open) {
      setCsvData([]);
      setError('');
      setPreviewData([]);
      setIsLoading(false);
    }
  }, [open]);

  const downloadTemplate = () => {
    // Create template data with example values
    const templateData = [
      {
        Summary: 'Example Task',
        Description: 'This is an example task description',
        CardStatus: 'To Do',
        DueDate: '2024-12-31',
        CardType: 'Task',
        Priority: 'Medium',
        AcceptanceCriteria: '1. Complete the task\n2. Test the functionality\n3. Document the changes',
        Estimate: '5',
        AssigneeName: 'John Doe'
      },
      {
        Summary: 'Bug Fix',
        Description: 'Fix the login issue',
        CardStatus: 'In Progress',
        DueDate: '2024-12-15',
        CardType: 'Defect',
        Priority: 'High',
        AcceptanceCriteria: '1. Fix the login bug\n2. Test the login flow\n3. Verify security',
        Estimate: '3',
        AssigneeName: 'Jane Smith'
      }
    ];

    // Convert to CSV
    const csv = Papa.unparse(templateData);
    
    // Create and download file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'card_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setError('');
    setCsvData([]);
    setPreviewData([]);

    // Check if file is CSV
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      setIsLoading(false);
      return;
    }

    Papa.parse(file, {
      complete: (results) => {
        setIsLoading(false);
        console.log('Raw CSV results:', results); // Debug log for raw results
        
        if (results.errors.length > 0) {
          setError('Error parsing CSV file: ' + results.errors[0].message);
          return;
        }

        // Get headers from the first row
        const headers = results.meta.fields || results.data[0];
        console.log('Headers found:', headers); // Debug log for headers

        if (!headers || !Array.isArray(headers)) {
          setError('Invalid CSV format: Headers not found');
          return;
        }

        const requiredHeaders = ['Summary', 'Description', 'CardStatus', 'DueDate', 'CardType', 'Priority', 'AcceptanceCriteria', 'Estimate', 'AssigneeName'];
        
        // Validate headers
        const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
        if (missingHeaders.length > 0) {
          setError(`Missing required columns: ${missingHeaders.join(', ')}`);
          return;
        }

        // Convert CSV data to card objects
        const cards = results.data
          .filter(row => row && Object.keys(row).length > 0) // Filter out empty rows based on object keys
          .map(row => {
            const card = {};
            headers.forEach((header, index) => {
              // Handle both array and object formats from Papa Parse
              const value = typeof row === 'object' && !Array.isArray(row) ? row[header] : row[index];
              card[header] = value || ''; // Handle empty values
            });
            return card;
          })
          .filter(card => {
            // Check if at least one required field has a value
            return requiredHeaders.some(header => card[header] && card[header].trim() !== '');
          });

        console.log('Processed cards:', cards); // Debug log for processed cards

        if (cards.length === 0) {
          setError('No valid data found in the CSV file. Please check the file format and try again.');
          return;
        }

        setCsvData(cards);
        setPreviewData(cards.slice(0, 5)); // Show first 5 rows as preview
      },
      header: true,
      skipEmptyLines: true,
      transformHeader: header => header.trim(), // Trim whitespace from headers
      transform: value => value ? value.trim() : '', // Trim whitespace from values
      dynamicTyping: false // Keep all values as strings
    });
  };

  const handleImport = () => {
    if (csvData.length === 0) {
      setError('No data to import');
      return;
    }
    console.log('Importing cards:', csvData); // Debug log
    onImport(csvData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Cards from CSV</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Upload CSV File'}
              </Button>
            </label>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              disabled={isLoading}
            >
              Download Template
            </Button>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Download the template CSV file to see the required format and example data
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Loading and parsing CSV file...
          </Alert>
        )}

        {previewData.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview (First 5 rows)
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {Object.keys(previewData[0]).map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.map((row, index) => (
                    <TableRow key={index}>
                      {Object.values(row).map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Total rows to import: {csvData.length}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleImport}
          variant="contained"
          color="primary"
          disabled={csvData.length === 0 || isLoading}
        >
          Import Cards
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportCardsDialog; 